import { DeckPrint, DeckViewer, Frame } from "../headliner/DeckShell";
import { Cover } from "./BuildCover";
import { StageScene } from "./BuildStage";
import { BuildSequence } from "./BuildSequence";
import { WhatItIs } from "../headliner/WhatItIs";
import { TEAM_ASSETS, TeamAndPartners } from "../headliner/TeamAndPartners";
import { brandOf, brandRootStyle, Wordmark } from "./brands";
import { LogoStamp } from "../headliner/brands";
import "./build.css";

/* Build Partnership («Vibe Coding Partnership») — søsterdeck til Headliner (samme skall og cover-ark), papir-tema med én mørk fotoslide.
   12 slides for kald utsendelse: cover · what it is · the stage (logo i salen) · the room (publikumsbevis) · the room runs on X ·
   why X · the idea · main stage · Build Lab · the day · the position · build this with us. Mottakerlag styrer logo, aksent, Why, Lab-navn. */

const recipient = brandOf;

/* 04 The room — publikumsbevis: hvem de 700 er (segmenter, ikke prosenter vi ikke har dekning for) og hvorfor rommet betyr noe kommersielt */
const ROOM = [
  ["C-suite & senior leaders", "The people who approve what gets built — and what gets bought."],
  ["Technology & product", "CIOs, CTOs, heads of AI and product — the ones who deploy."],
  ["Investors & builders", "Founders, scale-ups and the capital behind them."],
  ["Public sector & policy", "The institutions that set the pace for AI in Norway."],
];

const Room = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The room" mottaker={mottaker} className="bd-room">
    <div className="bd-big bd-room-big" data-testid="bd-room-700">700</div>
    <p className="bd-biglab bd-room-lab">seats · invitation-led · Grieghallen, Bergen</p>
    <p className="bd-room-cur" data-testid="bd-room-cur">Curated for people with the authority to buy, build and deploy AI.</p>
    <ul className="hd-ledger bd-ledger bd-room-ledger" data-testid="bd-room-segments">
      {ROOM.map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="bd-foot" data-testid="bd-room-foot"><b>700 people who today approve software could leave believing they can build it.</b> For {mottaker}: the buyers, the builders and the people who approve both — in one room, for one day.</p>
  </Frame>
);

/* 04 Why <mottaker> — tidlig, så mottakeren føler seg valgt. Fit, ikke tall. */
const WhyYou = ({ nr, mottaker }) => (
  <Frame nr={nr} label={`Why ${mottaker}`} mottaker={mottaker} className="bd-whyyou">
    <h2 className="bd-h96" data-testid="bd-whyyou-h">Why {mottaker}.</h2>
    <ul className="hd-ledger bd-ledger" data-testid="bd-whyyou-points">
      {recipient(mottaker).why(mottaker).map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <LogoStamp name={mottaker} sub="Build Partner · The vibe coding position · Norwegian AI Summit 2027" />
    <p className="bd-foot" data-testid="bd-whyyou-foot">Not «here is what {mottaker} does» — here is why this room is strategically interesting for {mottaker}.</p>
  </Frame>
);

/* 05 The idea — kjernen, med problem → prompt → iteration → working product → live */
const FLOW = [
  ["Problem", "A real business problem, chosen together with the partner"],
  ["Prompt", "Described in plain language, on stage"],
  ["Iteration", "Refined live, in front of the room"],
  ["Working product", "Interface, logic and data — running"],
  ["Live", "QR code on screen. Open it. Use it."],
];

const Idea = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The idea" mottaker={mottaker} className="bd-idea">
    <h2 className="bd-h88" data-testid="bd-idea-h">One problem.<br />Built live.<br />Shipped before<br />the session ends.</h2>
    <ol className="bd-flow" data-testid="bd-flow">
      {FLOW.map(([t, d], i) => (
        <li key={t}><span className="i">0{i + 1}</span><span className="t">{t}</span><span className="d">{d}</span></li>
      ))}
    </ol>
    <p className="bd-foot">Not a demo. A collective act of building.</p>
  </Frame>
);

/* 06 The main-stage moment — spectacle of capability, ikke tutorial */
const Stage = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The main-stage moment" mottaker={mottaker} className="bd-stage">
    <div className="bd-big" data-testid="bd-stage-min">20–30 <span className="u">min</span></div>
    <p className="bd-biglab">The live build · main stage</p>
    <div className="bd-stage-r">
      <span className="k">How it works</span>
      <p className="p" data-testid="bd-stage-p">A Norwegian business leader brings a real problem. {mottaker} builds it on the main stage. The room watches an idea move from language to working product.</p>
      <p className="l">One builder · one leader · one real problem</p>
    </div>
    <p className="bd-foot" data-testid="bd-stage-foot">Produced jointly with {mottaker}. <b>Real problem. Real build. Rehearsed infrastructure — not a scripted demo.</b></p>
  </Frame>
);

/* 07 The Build Lab — bemannet produktopplevelse, ikke stand */
const LAB = [
  ["Build stations", "10–15 stations with large screens"],
  ["Build clinics", "15-minute guided sessions, all day"],
  ["Experts on the floor", "Staffed by builders — not a stand, not a roll-up"],
  ["Live wall", "What the room is building, as it happens"],
];

const Lab = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Build Lab" mottaker={mottaker} className="bd-lab">
    <h2 className="bd-h88" data-testid="bd-lab-h">{recipient(mottaker).lab}.</h2>
    <p className="bd-lab-sub"><b>The main stage proves it. The Build Lab lets 700 people experience it.</b> People enter as software users. They leave as software builders.</p>
    <div className="hd-exec-grid" data-testid="bd-lab-points">
      {LAB.map(([t, d]) => (
        <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>
      ))}
    </div>
    <p className="bd-foot">Part of the AI Experience · open throughout the day</p>
  </Frame>
);

/* 08 The day — aktiveringen lever i en premium dag; Kevin som kvalitetsstempel */
const DAG = [
  ["09:00", "Arrival"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)"], ["11:30", "Leadership programme"], ["13:00", "AI Experience · Build Lab"],
  ["14:30", "The live build", true], ["16:00", "Closing keynote"], ["18:30", "Summit Dinner"],
];

const Day = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The day" mottaker={mottaker} className="hd-day bd-day">
    <h2 className="hd-h2" data-testid="bd-day-h">One venue. One programme.<br />The build lives inside a premium day.</h2>
    <ol className="hd-timeline bd-timeline" data-testid="bd-timeline">
      {DAG.map(([t, l, hi]) => (
        <li key={t} className={hi ? "hi" : ""}>
          <span className="t">{t}</span>
          <i className="pt" />
          <span className="l">{l}</span>
        </li>
      ))}
    </ol>
    <p className="hd-dayfoot">700 decision-makers moving through the same day — with the build as the hands-on moment.</p>
  </Frame>
);

/* 11 The position — én kategori, én Build Partner: rettigheter m/ innhold, definert eksklusivitet, før/under/etter */
const RIGHTS = (m) => [
  ["Main-stage moment", "The live build, 14:30 — in front of the whole room"],
  ["The Build Lab", "Staffed, hands-on, open all day"],
  ["Category exclusivity", "AI-native app-building / vibe coding — scope agreed in the terms"],
  ["Content system", `Main-stage hero film · 3–5 short-form edits · build case study · reusable ${m} template · full session on demand · partner-owned clips`],
];
const BDA = [
  ["Before", "Optional build challenge. Audience submissions: what would you build?"],
  ["During", "The live build on the main stage. The Build Lab throughout the day."],
  ["After", "Builds · clips · templates · editorial content — «Built at Norwegian AI Summit»."],
];

const Position = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The position" mottaker={mottaker} className="hd-one bd-one">
    <h2 className="hd-one-h" data-testid="bd-one-h">One category.<br />One Build Partner.</h2>
    <p className="hd-one-sub">One moment in the room. A content system after it. The vibe coding position at Norwegian AI Summit: one platform owns the story of turning ideas into working software.</p>
    <ul className="hd-one-pts bd-one-pts" data-testid="bd-one-points">
      {RIGHTS(mottaker).map(([t, d]) => <li key={t}><b>{t}</b><span>{d}</span></li>)}
    </ul>
    <ol className="bd-bda" data-testid="bd-bda">
      {BDA.map(([h, p]) => <li key={h}><b>{h}</b><span>{p}</span></li>)}
    </ol>
    <p className="hd-one-univ" data-testid="bd-one-foot">Exclusive within the AI-native app-building / vibe-coding category<i> · </i>scope agreed in the partnership terms.</p>
  </Frame>
);

/* 12 Build this with us — Norwegian AI Summit tar produksjonsbyrden (venstre), partneren bidrar med lite og presist (høyre);
   ingen pris, lav-friksjons neste steg; team + leveransepartnere nederst */
const US = ["Stage production, staffing & logistics", "The audience — 700 curated seats", "The physical Build Lab", "Broadcast & content production", "Exclusive Build Partner position"];
const YOU = ["One senior builder / speaker", "Platform support & credits", "Brand collaboration & co-marketing", "Partnership investment"];

const Ask = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Next step" mottaker={mottaker} className="bd-ask">
    <h2 className="bd-h88" data-testid="bd-ask-h">Build this with us.</h2>
    <div className="bd-ask-body">
      <div className="bd-two" data-testid="bd-ask-cols">
        <div className="col">
          <span className="k">Norwegian AI Summit produces</span>
          <ul>{US.map((u) => <li key={u}>{u}</li>)}</ul>
        </div>
        <div className="col">
          <span className="k">{mottaker} provides</span>
          <ul>{YOU.map((y) => <li key={y}>{y}</li>)}</ul>
        </div>
      </div>
      <p className="bd-ask-p" data-testid="bd-ask-p">We would like {mottaker} to own the Build category at Norwegian AI Summit 2027.</p>
      <p className="bd-terms" data-testid="bd-ask-next">Next step — a 30-minute working session on category fit, activation and commercial terms.</p>
      <p className="bd-proof" data-testid="bd-ask-proof"><b>Status, plainly:</b> Grieghallen — offer received · Unite — proposed delivery partner · Apriil — proposed design partner · Kevin O'Leary — availability confirmed for 13 April 2027, not yet contracted · 700 seats — target, invitation-led.</p>
    </div>
    <TeamAndPartners />
    <p className="bd-x bd-x-ask" data-testid="bd-ask-lockup"><b>Norwegian AI Summit</b><i>×</i><Wordmark name={mottaker} h={30} /></p>
  </Frame>
);

const SLIDES = [Cover, WhatItIs, StageScene, Room, BuildSequence, WhyYou, Idea, Stage, Lab, Day, Position, Ask];

const ASSETS = ["/images/headliner/kevin-src-hi.webp", "/images/grieghallen.webp", "/images/headliner/kevin-cover-clean.webp", "/images/what-room-v4.webp", "/images/build-stage.webp", "/images/build-stage-lovable.webp", "/images/build-stage-replit.webp", "/images/build-stage-emergent.webp", "/images/build-stage-base44.webp", ...TEAM_ASSETS];

export function BuildPrint() {
  return <DeckPrint slides={SLIDES} fallback="Lovable" theme="paper" venue="none" rootStyle={brandRootStyle} />;
}

export default function BuildDeck({ mottaker = "Lovable", publicMode = false }) {
  return <DeckViewer deck="build" slides={SLIDES} fallback={mottaker} filePrefix="Norwegian-AI-Summit-Build-Partnership" assets={ASSETS} theme="paper" venue="none" categoryField={false} publicMode={publicMode} rootStyle={brandRootStyle} />;
}
